import React, { useEffect } from 'react';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { FORM_MODES } from 'config/formModes';
import { useCampaignGroupEnabled } from 'modules/campaigns/modules/campaigns-group-form/hooks/useCampaignGroupEnabled';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { useMultiLayerDrawerContext } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/components/MultiLayerDrawerProvider/MultiLayerDrawerProvider';
import { usePaceAheadEnabled } from '../../../campaigns-group-form/hooks/usePaceAheadEnabled';
import { BUDGET_TYPES } from '../../config/budgetTypes';
import { Budget } from './Budget';
import { getEmptyBudgetObject } from './const/emptyBudgetObject';
import { useBudgetFormFieldValue } from './hooks/useBudgetFormFieldValue';
import { useIsCampaignPartOfFullySharedBudget } from './hooks/useIsCampaignPartOfFullySharedBudget';
import { useNoneSpendingLimitEnabled } from './hooks/useNoneSpendingLimitEnabled';

export const CampaignBudget = ({ budgetRestrictions }) => {
    const { isCurrentlyDirty, mode } = useFormDataContext();
    const { onChange: onBudgetChange, isDirty: isBudgetDirty } = useBudgetFormFieldValue();
    const { value: isPartOfSharedBudgetCampaignsGroup } = useFormFieldValue({
        field: 'isPartOfSharedBudgetCampaignsGroup',
    });
    const { value: spent } = useFormFieldValue({ field: 'totalSpent' });
    const isCampaignGroupChanged = isCurrentlyDirty('campaignGroupInfo.campaignGroupId');
    const campaignGroupBudget = budgetRestrictions?.campaignGroupBudget;
    const campaignGroupExtensionEnabled = useCampaignGroupEnabled();
    const notPermitted = !usePermissions('CAMPAIGN_EDIT');
    const isDisabled =
        notPermitted ||
        (mode === FORM_MODES.EDIT && isPartOfSharedBudgetCampaignsGroup && !campaignGroupExtensionEnabled);
    const isNoneSpendingLimitEnabled = useNoneSpendingLimitEnabled();
    const isBudgetHidden = useIsCampaignPartOfFullySharedBudget();
    const { entityData, setEntityData } = useMultiLayerDrawerContext();
    const isPaceAheadEnabled = usePaceAheadEnabled();

    useEffect(() => {
        if (entityData && typeof entityData.budgetValue === 'number') {
            onBudgetChange(prev => ({ ...prev, value: entityData.budgetValue }));
        }
    }, [entityData, onBudgetChange, setEntityData]);

    useEffect(() => {
        if (!isCampaignGroupChanged) {
            return;
        }
        if (campaignGroupBudget?.type === BUDGET_TYPES.UNLIMITED) {
            if (isBudgetDirty) {
                onBudgetChange(prev =>
                    getEmptyBudgetObject({ prev, type: BUDGET_TYPES.DAILY, isNoneSpendingLimitEnabled })
                );
            }
            return;
        }
        if (!campaignGroupBudget) {
            return;
        }
        const newBudget = cloneDeep(campaignGroupBudget);
        onBudgetChange(newBudget);
    }, [isCampaignGroupChanged, campaignGroupBudget, onBudgetChange, isNoneSpendingLimitEnabled, isBudgetDirty]);

    return (
        <Budget
            spent={spent}
            entityTypeMessagePrefix="campaign"
            isDisabled={isDisabled}
            isHidden={isBudgetHidden}
            helpTextId={isPartOfSharedBudgetCampaignsGroup ? 'campaign.editor.budget.help.disabledInGroup' : ''}
            showPaceAhead={isPaceAheadEnabled}
        />
    );
};

CampaignBudget.propTypes = {
    currency: PropTypes.string,
};
